import { useEffect, useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { supabase } from "@/integrations/supabase/client";
import { Plus, Edit, Trash2, Calendar } from "lucide-react";
import { toast } from "sonner";

interface PriceTable {
  id: string;
  name: string;
  effective_from: string;
  effective_until: string | null;
  is_current: boolean;
  created_at: string;
}

interface PriceTablePlan {
  id: string;
  price_table_id: string;
  plan_code: string;
  price_cents: number;
}

interface SubscriptionPlan {
  code: string;
  name: string;
}

export default function TabelasPreco() {
  const [loading, setLoading] = useState(true);
  const [priceTables, setPriceTables] = useState<PriceTable[]>([]);
  const [plans, setPlans] = useState<SubscriptionPlan[]>([]);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingTable, setEditingTable] = useState<PriceTable | null>(null);
  const [planPrices, setPlanPrices] = useState<Record<string, number>>({});

  const [formData, setFormData] = useState({
    name: "",
    effective_from: "",
    effective_until: "",
    is_current: false,
  });

  useEffect(() => {
    loadData();
  }, []);

  async function loadData() {
    try {
      const [tablesRes, plansRes] = await Promise.all([
        supabase
          .from("price_tables")
          .select("*")
          .order("effective_from", { ascending: false }),
        supabase
          .from("subscription_plans")
          .select("code, name")
          .order("display_order"),
      ]);

      if (tablesRes.error) throw tablesRes.error;
      if (plansRes.error) throw plansRes.error;

      setPriceTables((tablesRes.data || []) as PriceTable[]);
      setPlans((plansRes.data || []) as SubscriptionPlan[]);
    } catch (error) {
      console.error("Error loading data:", error);
      toast.error("Erro ao carregar dados");
    } finally {
      setLoading(false);
    }
  }

  function openCreateDialog() {
    setEditingTable(null);
    setFormData({
      name: "",
      effective_from: new Date().toISOString().split("T")[0],
      effective_until: "",
      is_current: false,
    });
    setPlanPrices({});
    setIsDialogOpen(true);
  }

  async function openEditDialog(table: PriceTable) {
    setEditingTable(table);
    setFormData({
      name: table.name,
      effective_from: table.effective_from,
      effective_until: table.effective_until || "",
      is_current: table.is_current,
    });

    const { data } = await supabase
      .from("price_table_plans")
      .select("plan_code, price_cents")
      .eq("price_table_id", table.id);

    const prices: Record<string, number> = {};
    (data || []).forEach((p) => {
      prices[p.plan_code] = p.price_cents;
    });
    setPlanPrices(prices);
    setIsDialogOpen(true);
  }

  async function handleSubmitTable(e: React.FormEvent) {
    e.preventDefault();

    const payload = {
      name: formData.name,
      effective_from: formData.effective_from,
      effective_until: formData.effective_until || null,
      is_current: formData.is_current,
    };

    try {
      if (editingTable) {
        const { error } = await supabase
          .from("price_tables")
          .update(payload)
          .eq("id", editingTable.id);
        if (error) throw error;
        toast.success("Tabela atualizada com sucesso");
      } else {
        const { data: newTable, error } = await supabase
          .from("price_tables")
          .insert(payload)
          .select("id")
          .single();
        if (error) throw error;
        toast.success("Tabela criada com sucesso");
        if (newTable) {
          await savePlanPrices(newTable.id);
        }
      }

      if (editingTable) {
        await savePlanPrices(editingTable.id);
      }

      setIsDialogOpen(false);
      loadData();
    } catch (error) {
      console.error("Error saving:", error);
      toast.error("Erro ao salvar tabela");
    }
  }

  async function savePlanPrices(priceTableId: string) {
    const plansToSave = Object.entries(planPrices).filter(
      ([_, cents]) => cents > 0
    );
    if (plansToSave.length === 0) return;

    try {
      const { error: delError } = await supabase
        .from("price_table_plans")
        .delete()
        .eq("price_table_id", priceTableId);
      if (delError) throw delError;

      const inserts = plansToSave.map(([plan_code, price_cents]) => ({
        price_table_id: priceTableId,
        plan_code,
        price_cents,
      }));

      const { error: insError } = await supabase
        .from("price_table_plans")
        .insert(inserts);
      if (insError) throw insError;
    } catch (error) {
      console.error("Error saving plan prices:", error);
      throw error;
    }
  }

  async function handleDelete(id: string) {
    if (!confirm("Tem certeza que deseja excluir esta tabela de preços? Os preços vinculados também serão removidos."))
      return;

    try {
      const { error } = await supabase.from("price_tables").delete().eq("id", id);
      if (error) throw error;
      toast.success("Tabela excluída com sucesso");
      loadData();
    } catch (error) {
      console.error("Error deleting:", error);
      toast.error("Erro ao excluir tabela");
    }
  }

  const formatPrice = (cents: number) =>
    new Intl.NumberFormat("pt-BR", {
      style: "currency",
      currency: "BRL",
    }).format(cents / 100);

  const formatDate = (d: string) =>
    new Intl.DateTimeFormat("pt-BR").format(new Date(d));

  if (loading) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Tabelas de Preço</h1>
          <p className="text-muted-foreground">
            Gerencie tabelas de preço por vigência
          </p>
        </div>
        <Skeleton className="h-64" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Tabelas de Preço</h1>
          <p className="text-muted-foreground">
            Gerencie tabelas de preço por vigência. Usuários são vinculados à
            tabela vigente na data de cadastro.
          </p>
        </div>
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogTrigger asChild>
            <Button onClick={openCreateDialog}>
              <Plus className="h-4 w-4 mr-2" />
              Nova Tabela
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>
                {editingTable ? "Editar Tabela de Preço" : "Nova Tabela de Preço"}
              </DialogTitle>
              <DialogDescription>
                {editingTable
                  ? "Altere os dados e preços por plano"
                  : "Crie uma nova tabela com período de vigência"}
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmitTable} className="space-y-6">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="name">Nome</Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) =>
                      setFormData({ ...formData, name: e.target.value })
                    }
                    placeholder="ex: Tabela 2026"
                    required
                  />
                </div>
                <div className="space-y-2 flex items-end">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.is_current}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          is_current: e.target.checked,
                        })
                      }
                      className="rounded"
                    />
                    <span className="text-sm">Tabela atual (padrão)</span>
                  </label>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="effective_from">Vigência início</Label>
                  <Input
                    id="effective_from"
                    type="date"
                    value={formData.effective_from}
                    onChange={(e) =>
                      setFormData({ ...formData, effective_from: e.target.value })
                    }
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="effective_until">Vigência fim</Label>
                  <Input
                    id="effective_until"
                    type="date"
                    value={formData.effective_until}
                    onChange={(e) =>
                      setFormData({ ...formData, effective_until: e.target.value })
                    }
                    placeholder="Deixe vazio para sem data fim"
                  />
                </div>
              </div>

              <div className="space-y-4">
                <h4 className="font-medium">Preços por plano</h4>
                <p className="text-sm text-muted-foreground">
                  Defina o valor em centavos para cada plano. Ex: 3990 = R$
                  39,90
                </p>
                <div className="border rounded-lg divide-y">
                  {plans.map((plan) => (
                    <div
                      key={plan.code}
                      className="flex items-center justify-between p-3"
                    >
                      <span className="font-medium">{plan.name}</span>
                      <Input
                        type="number"
                        placeholder="0"
                        className="w-32"
                        value={planPrices[plan.code] ?? ""}
                        onChange={(e) =>
                          setPlanPrices({
                            ...planPrices,
                            [plan.code]: parseInt(e.target.value) || 0,
                          })
                        }
                      />
                    </div>
                  ))}
                </div>
              </div>

              <div className="flex justify-end gap-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setIsDialogOpen(false)}
                >
                  Cancelar
                </Button>
                <Button type="submit">
                  {editingTable ? "Salvar" : "Criar"}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Tabelas cadastradas</CardTitle>
          <CardDescription>
            Ordene por vigência. A primeira tabela vigente na data de cadastro
            é vinculada ao usuário.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Nome</TableHead>
                <TableHead>Vigência início</TableHead>
                <TableHead>Vigência fim</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Ações</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {priceTables.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} className="text-center py-8 text-muted-foreground">
                    Nenhuma tabela cadastrada. Clique em Nova Tabela para criar.
                  </TableCell>
                </TableRow>
              ) : (
                priceTables.map((table) => (
                  <TableRow key={table.id}>
                    <TableCell className="font-medium">{table.name}</TableCell>
                    <TableCell>
                      <span className="flex items-center gap-1">
                        <Calendar className="h-4 w-4 text-muted-foreground" />
                        {formatDate(table.effective_from)}
                      </span>
                    </TableCell>
                    <TableCell>
                      {table.effective_until
                        ? formatDate(table.effective_until)
                        : "—"}
                    </TableCell>
                    <TableCell>
                      {table.is_current ? (
                        <Badge variant="default">Atual</Badge>
                      ) : (
                        <Badge variant="secondary">Histórica</Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        <Button
                          variant="outline"
                          size="icon"
                          onClick={() => openEditDialog(table)}
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="outline"
                          size="icon"
                          onClick={() => handleDelete(table.id)}
                          className="text-destructive hover:text-destructive"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
